/**
 * /manage — the in-app Manage console (formerly the standalone admin app).
 *
 * Org-scoped administration in the mockup's Manage shell: a left section nav
 * (Organization + Account groups) with the user/mode switcher in the footer,
 * and the active section rendered on the right via <Outlet/>. Gated to org
 * owners/admins. The functional sections reuse the admin API verbatim; Billing/
 * Credits/Integrations are mock pages (no backend yet).
 */

import { Wordmark } from "@chai/ui";
import { Link, Outlet, createFileRoute, useLocation } from "@tanstack/react-router";
import {
  Activity,
  AppWindow,
  Coins,
  CreditCard,
  Database,
  Folder,
  FolderKanban,
  Globe,
  PenLine,
  Plug,
  Rss,
  Target,
  Users,
} from "lucide-react";

import { useAuth } from "@/auth";
import { OrgPicker } from "@/components/manage/OrgPicker";
import {
  OrgProvider,
  useIsDemoManage,
  useManageMemberships,
  useOrg,
} from "@/components/manage/org-context";
import { ViewSwitcher } from "@/components/plan/ViewSwitcher";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/manage")({
  component: ManageRoute,
});

const NAV_GROUPS = [
  {
    label: "Organization",
    items: [
      { to: "/manage/members", icon: Users, label: "Members" },
      { to: "/manage/workspaces", icon: Folder, label: "Workspaces" },
      { to: "/manage/projects", icon: FolderKanban, label: "Projects" },
      { to: "/manage/objectives", icon: Target, label: "Objectives" },
      { to: "/manage/datasets", icon: Database, label: "Datasets" },
      { to: "/manage/connections", icon: Plug, label: "Connections" },
      { to: "/manage/sites", icon: Globe, label: "Web artifacts" },
      { to: "/manage/apps", icon: AppWindow, label: "Apps" },
      { to: "/manage/llm-logs", icon: Activity, label: "LLM logs" },
      {
        to: "/manage/platform-writes",
        icon: PenLine,
        label: "Made by the platform",
      },
      { to: "/manage/data-events", icon: Rss, label: "Data events" },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/manage/billing", icon: CreditCard, label: "Billing" },
      { to: "/manage/credits", icon: Coins, label: "Credits" },
    ],
  },
] as const;

// Sections a demo session can browse (read-only, tenant-scoped). Everything
// else — web artifacts, apps, LLM logs, billing/credits/integrations — is
// demo-403'd or host-org only, so it never shows in a demo's nav.
const DEMO_NAV = new Set<string>([
  "/manage/members",
  "/manage/workspaces",
  "/manage/projects",
  "/manage/objectives",
  "/manage/datasets",
]);

function ManageRoute() {
  return (
    <OrgProvider>
      <ManageShell />
    </OrgProvider>
  );
}

function ManageShell() {
  const auth = useAuth();
  const manageMemberships = useManageMemberships();
  const isDemo = useIsDemoManage();
  const { orgId } = useOrg();
  const location = useLocation();

  if (auth.isLoading) return <CenterNote>Loading…</CenterNote>;
  if (!auth.me) return <CenterNote>Sign in to access Manage.</CenterNote>;
  if (auth.me.user.artifact_only) {
    return (
      <CenterNote>
        This account opens the pages shared with it.{" "}
        <a href="/shared" className="font-medium text-foreground underline underline-offset-4">
          See them
        </a>
      </CenterNote>
    );
  }
  if (manageMemberships.length === 0) {
    return (
      <CenterNote>
        <span className="font-medium text-foreground">Admin role required.</span>
        <br />
        This account isn't an owner or admin of any organization.
      </CenterNote>
    );
  }

  const navGroups = isDemo
    ? NAV_GROUPS.map((group) => ({
        label: group.label,
        items: group.items.filter((item) => DEMO_NAV.has(item.to)),
      })).filter((group) => group.items.length > 0)
    : NAV_GROUPS;

  return (
    <div className="flex h-screen overflow-hidden bg-[var(--color-surface)]">
      <aside className="flex w-[236px] shrink-0 flex-col border-r border-[var(--color-hairline)] bg-[var(--color-surface-soft)]">
        <div className="flex h-14 items-center justify-between gap-2 px-4">
          <Wordmark />
          <span className="rounded-sm bg-foreground/5 px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            manage
          </span>
        </div>

        <div className="border-t border-[var(--color-hairline)] px-4 py-3">
          <OrgPicker />
        </div>

        {/* data-tour anchor: the demo guided tour spotlights the Manage
            section nav. */}
        <nav className="flex-1 overflow-y-auto px-2 py-2" data-tour="manage-nav">
          {navGroups.map((group) => (
            <div key={group.label} className="mb-3">
              <div className="px-2.5 pb-1 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--color-slate)]">
                {group.label}
              </div>
              <ul className="flex flex-col gap-0.5">
                {group.items.map((item) => {
                  const active = location.pathname.startsWith(item.to);
                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        className={cn(
                          "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition",
                          active
                            ? "bg-[var(--color-card-tint-lavender)] font-medium text-[var(--color-brand-purple-800)]"
                            : "text-[var(--color-charcoal)] hover:bg-foreground/5",
                        )}
                      >
                        <item.icon
                          className={cn(
                            "size-4",
                            active
                              ? "text-[var(--color-brand-purple-800)]"
                              : "text-muted-foreground",
                          )}
                        />
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="border-t border-sidebar-border p-2">
          <ViewSwitcher mode="manage" />
        </div>
      </aside>

      <main className="flex-1 overflow-auto bg-[var(--color-canvas)]">
        {orgId ? <Outlet /> : <CenterNote>Loading org…</CenterNote>}
      </main>
    </div>
  );
}

function CenterNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-screen items-center justify-center px-6 text-center text-sm text-muted-foreground">
      <p>{children}</p>
    </div>
  );
}
